import React from 'react'
import { IconContext } from 'react-icons'
import { IoCloseSharp } from 'react-icons/io5'
import styled, { css } from 'styled-components'
import { ListType, Scalars } from 'types/graphql'
import { media } from 'utils/media'
import { Button } from 'lib/Button/Button'
import { useFloatingPopoverController } from 'lib/FloatingPopover'
import { useCurrentPageBelongsToMe } from 'modules/auth/useCurrentPageBelongsToMe'
import { TestedPrompt } from 'modules/reviews/common/components/TestedPrompt'
import { useIsBrandInList } from '../queries/useBrandLists'
import { useToggleBrandInList } from '../queries/useToggleBrandInList'
import { AddToListButton } from './AddToListButton'
import { TestedButton as TestedButtonOriginal } from './TestedButton'
import { WantToTestButton as WantToTestButtonOriginal } from './WantToTestButton'
import { WantToTestPrompt } from './WantToTestPrompt'
import { WriteOrEditReviewButton as WriteOrEditReviewButtonOriginal } from './WriteOrEditReviewButton'

export const ListActionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  grid-template-areas: 'review test list';
  grid-gap: 12px;

  ${media.phoneDown} {
    width: 100%;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'review review'
      'test list';
  }

  ${media.smallPhoneDown} {
    width: 100%;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'review list'
      'test test';
  }
`

type ButtonProps = { $testedIsActive: boolean; $wantToTestIsActive: boolean }

const WantToTestButton = styled(WantToTestButtonOriginal)<ButtonProps>`
  ${(props) =>
    !props.$testedIsActive &&
    css`
      &:hover {
        z-index: 1;
      }
    `}

  ${media.tabletDown} {
    flex: 1 1 50%;
    white-space: nowrap;
  }
`

const TestedButton = styled(TestedButtonOriginal)<ButtonProps>`
  && {
    margin-left: ${(props) => (props.$wantToTestIsActive && props.$testedIsActive ? '-2px' : '-1px')};

    ${media.tabletDown} {
      flex: 1 1 50%;
      white-space: nowrap;
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  grid-area: test;
  ${media.smallPhoneDown} {
    flex-wrap: wrap;
    row-gap: 12px;
  }
`

const AddToListContainer = styled.div`
  grid-area: list;
`

const WriteOrEditReviewButton = styled(WriteOrEditReviewButtonOriginal)`
  grid-area: review;
`

interface Props {
  brandId: Scalars['ID']
  brandTitle: string
  brandSlug: string
  currentListId?: number
  currentListType?: ListType
  className?: string
  reviewId?: Scalars['ID']
}
export const ListActions = ({ brandId, brandTitle, brandSlug, currentListId, currentListType, className, reviewId }: Props) => {
  const { currentPageBelongsToMe } = useCurrentPageBelongsToMe()
  const { toggleBrandInList } = useToggleBrandInList()
  const wantToTestControls = useFloatingPopoverController()
  const testedControls = useFloatingPopoverController()
  const inWantToTestList = useIsBrandInList(brandId, 'WantToTest')
  const inTestedList = useIsBrandInList(brandId, 'Tested')

  const toggleTestedPrompt = (inTestedList: boolean) => {
    if (reviewId) return

    if (testedControls.open) {
      testedControls.setOpen(false)
    }

    if (!testedControls.open && !inTestedList) {
      testedControls.setOpen(true)
    }
  }

  const toggleWantToTestPrompt = (inWantToTestList: boolean) => {
    if (reviewId) return

    if (wantToTestControls.open) {
      wantToTestControls.setOpen(false)
    }

    if (!wantToTestControls.open && !inWantToTestList) {
      wantToTestControls.setOpen(true)
    }
  }

  if (currentListType === ListType.Spotted) {
    return null
  }

  if (currentListId && currentListType && currentPageBelongsToMe) {
    return (
      <ListActionsContainer className={className}>
        <Button
          onClick={toggleBrandInList({
            brandId,
            listType: currentListType,
            listId: currentListId
          })}
          color="secondary"
          startIcon={
            <IconContext.Provider
              value={{
                style: {
                  height: 20,
                  width: 20,
                  marginRight: 6,
                  marginTop: -2
                }
              }}>
              <IoCloseSharp />
            </IconContext.Provider>
          }
          size="small"
          label="Remove"
        />
      </ListActionsContainer>
    )
  }

  return (
    <ListActionsContainer className={className}>
      <WriteOrEditReviewButton brandId={brandId} brandTitle={brandTitle} brandSlug={brandSlug} reviewId={reviewId} />

      <ButtonGroup>
        <WantToTestPrompt controls={wantToTestControls}>
          <WantToTestButton
            onClick={toggleWantToTestPrompt}
            brandId={brandId}
            $testedIsActive={inTestedList}
            $wantToTestIsActive={inWantToTestList}
          />
        </WantToTestPrompt>

        <TestedPrompt controls={testedControls} brandId={brandId} brandTitle={brandTitle} brandSlug={brandSlug} skip={!!reviewId}>
          <TestedButton onClick={toggleTestedPrompt} brandId={brandId} $testedIsActive={inTestedList} $wantToTestIsActive={inWantToTestList} />
        </TestedPrompt>
      </ButtonGroup>

      <AddToListContainer>
        <AddToListButton brandTitle={brandTitle} brandId={brandId} />
      </AddToListContainer>
    </ListActionsContainer>
  )
}
